// src/pages/league-dashboard/ManualOverrides.js
import React, { useState, useEffect } from 'react';
import { Shield, FileEdit, AlertCircle, Loader, Check, DollarSign, Wallet, ArrowLeftRight, Users } from 'lucide-react';
import { DynastyTable } from '../../services/tableService';
import { dynastyTheme } from '../../services/colorService';
import { createPositionEligibilityColumns } from '../../services/tables/positionEligibilityColumns';
import { createCommissionerContractColumns } from '../../services/tables/commissionerContractColumns';
import PlayerEligibilityModal from '../../components/position-eligibility/PlayerEligibilityModal';
import { leaguesAPI } from '../../services/apiService';
import CommissionerCashTools from '../../components/commissioner/CommissionerCashTools';
import DraftPicksOverride from '../../components/commissioner/DraftPicksOverride';
import MinorLeaguePicksOverride from '../../components/commissioner/MinorLeaguePicksOverride';

/**
 * Manual Overrides Page - Commissioner-only tools
 *
 * Tab 1: Contracts/Salaries - Override player contracts and salaries
 * Tab 2: Position Eligibility - Grant custom position eligibility
 * Tab 3: Cash Tools - Manual cash adjustments
 * Tab 4: Draft Picks - Commissioner pick reassignment
 * Tab 5: ML Draft Picks - Minor league draft pick slot reassignment
 */
const ManualOverrides = ({ leagueId, league, teams }) => {
  const [activeTab, setActiveTab] = useState('contracts');

  // Check if user is commissioner
  if (league?.role !== 'commissioner') {
    return (
      <div className={dynastyTheme.components.card.base + ' p-12'}>
        <div className="text-center space-y-4">
          <AlertCircle className={`w-16 h-16 mx-auto ${dynastyTheme.classes.text.warning}`} />
          <h2 className={`text-2xl font-bold ${dynastyTheme.classes.text.white}`}>
            Commissioner Access Only
          </h2>
          <p className={dynastyTheme.classes.text.neutralLight}>
            Only the league commissioner can access manual overrides
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={dynastyTheme.components.card.highlighted}>
        <div className="py-6 px-6">
          <div className="flex items-center gap-3">
            <FileEdit className={`w-8 h-8 ${dynastyTheme.classes.text.primary}`} />
            <h1 className={`text-4xl font-bold ${dynastyTheme.classes.text.white}`}>
              Manual Overrides
            </h1>
          </div>
          <p className={`${dynastyTheme.classes.text.neutralLight} mt-2 max-w-3xl`}>
            Commissioner tools for manually adjusting player contracts, salaries, and position eligibility.
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className={dynastyTheme.components.card.base}>
        <div className="flex border-b border-gray-700">
          <button
            onClick={() => setActiveTab('contracts')}
            className={`px-6 py-3 font-medium transition-colors border-b-2 ${
              activeTab === 'contracts'
                ? 'border-yellow-400 text-yellow-400'
                : 'border-transparent text-gray-400 hover:text-gray-300'
            }`}
          >
            <DollarSign className="w-4 h-4 inline mr-2" />
            Contracts / Salaries
          </button>
          <button
            onClick={() => setActiveTab('position-eligibility')}
            className={`px-6 py-3 font-medium transition-colors border-b-2 ${
              activeTab === 'position-eligibility'
                ? 'border-yellow-400 text-yellow-400'
                : 'border-transparent text-gray-400 hover:text-gray-300'
            }`}
          >
            <Shield className="w-4 h-4 inline mr-2" />
            Position Eligibility
          </button>
          <button
            onClick={() => setActiveTab('cash-tools')}
            className={`px-6 py-3 font-medium transition-colors border-b-2 ${
              activeTab === 'cash-tools'
                ? 'border-yellow-400 text-yellow-400'
                : 'border-transparent text-gray-400 hover:text-gray-300'
            }`}
          >
            <Wallet className="w-4 h-4 inline mr-2" />
            Cash Tools
          </button>
          <button
            onClick={() => setActiveTab('draft-picks')}
            className={`px-6 py-3 font-medium transition-colors border-b-2 ${
              activeTab === 'draft-picks'
                ? 'border-yellow-400 text-yellow-400'
                : 'border-transparent text-gray-400 hover:text-gray-300'
            }`}
          >
            <ArrowLeftRight className="w-4 h-4 inline mr-2" />
            Draft Picks
          </button>
          <button
            onClick={() => setActiveTab('ml-draft-picks')}
            className={`px-6 py-3 font-medium transition-colors border-b-2 ${
              activeTab === 'ml-draft-picks'
                ? 'border-yellow-400 text-yellow-400'
                : 'border-transparent text-gray-400 hover:text-gray-300'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            ML Draft Picks
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'contracts' && (
        <ContractsTab leagueId={leagueId} league={league} />
      )}

      {activeTab === 'position-eligibility' && (
        <PositionEligibilityTab leagueId={leagueId} league={league} teams={teams} />
      )}

      {activeTab === 'cash-tools' && (
        <CommissionerCashTools leagueId={leagueId} />
      )}

      {activeTab === 'draft-picks' && (
        <DraftPicksOverride leagueId={leagueId} teams={teams} league={league} />
      )}

      {activeTab === 'ml-draft-picks' && (
        <MinorLeaguePicksOverride leagueId={leagueId} teams={teams} league={league} />
      )}

    </div>
  );
};

/**
 * Contracts/Salaries Tab
 * Inline save per row — no batch mode.
 */
const DEFAULT_EXTENSION_RULES = [
  { years: 1, cost_increase: 0, description: 'Same price, 1 year' },
  { years: 2, cost_increase: 10, description: '+$10, 2 years' },
  { years: 3, cost_increase: 20, description: '+$20, 3 years' },
  { years: 4, cost_increase: 30, description: '+$30, 4 years' },
  { years: 5, cost_increase: 40, description: '+$40, 5 years' }
];

/**
 * Derive contract_years from contract_type. Mirrors backend logic.
 * Exported as a named export per Bible §14 frontend testing convention
 * so unit tests can target the helper directly without mock circularity
 * or inline reimplementation.
 */
export const contractTypeToYears = (contractType) => {
  if (!contractType || contractType === 'NOT_SET') return 1;
  if (contractType === 'pending') return 0;
  if (contractType === 'none_minors') return 0;
  if (contractType === '1') return 1;
  if (contractType === '2') return 2;
  if (contractType === 'OP') return 1;
  if (contractType.startsWith('OP')) {
    const n = parseInt(contractType.slice(2), 10);
    return isNaN(n) ? 1 : n + 1;
  }
  return 1;
};

const ContractsTab = ({ leagueId, league }) => {
  // State
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [salarySettings, setSalarySettings] = useState(null);

  // Editing state
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [editedContracts, setEditedContracts] = useState({});

  // Load teams + salary settings on mount
  useEffect(() => {
    loadTeams();
    loadSalarySettings();
  }, [leagueId]);

  // Load team contracts when team is selected
  useEffect(() => {
    if (selectedTeamId) {
      loadTeamContracts(selectedTeamId);
    }
  }, [selectedTeamId]);

  const loadSalarySettings = async () => {
    try {
      const response = await leaguesAPI.getSalarySettings(leagueId);
      if (response.success && response.settings) {
        const settings = response.settings;
        if (!settings.extension_rules || settings.extension_rules.length === 0) {
          settings.extension_rules = DEFAULT_EXTENSION_RULES;
        }
        setSalarySettings(settings);
      }
    } catch (err) {
      console.error('Error loading salary settings:', err);
    }
  };

  const loadTeams = async () => {
    try {
      setLoading(true);
      const response = await leaguesAPI.getLeagueTeamsForContracts(leagueId);

      if (response.success) {
        setTeams(response.teams || []);
        if (response.teams && response.teams.length > 0) {
          setSelectedTeamId(response.teams[0].team_id);
        }
      } else {
        setError('Failed to load teams');
        setTimeout(() => setError(''), 5000);
      }
    } catch (error) {
      console.error('Error loading teams:', error);
      setError('Failed to load teams');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const loadTeamContracts = async (teamId) => {
    try {
      setLoading(true);
      setError('');

      const response = await leaguesAPI.getTeamContracts(leagueId, teamId);

      if (response.success) {
        setContracts(response.contracts || []);
        setEditedContracts({});
        setEditingPlayer(null);
      } else {
        setError('Failed to load team contracts');
        setTimeout(() => setError(''), 5000);
      }
    } catch (error) {
      console.error('Error loading team contracts:', error);
      setError('Failed to load team contracts');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleTeamChange = (e) => {
    setSelectedTeamId(e.target.value);
    setEditingPlayer(null);
    setEditedContracts({});
  };

  const handleStartEdit = (row) => {
    const lpId = row.ids.league_player;
    const fin = row.league_context?.financial || {};
    const minSalary = salarySettings?.min_salary || 2;
    // Map null type + 0 years to 'pending' for dropdown display
    const isPending = !fin.contract_type && fin.contract_years === 0;
    setEditingPlayer(lpId);
    setEditedContracts({
      ...editedContracts,
      [lpId]: {
        salary: fin.contract_salary ?? minSalary,
        contract_years: fin.contract_years ?? 1,
        contract_type: isPending ? 'pending' : (fin.contract_type || 'NOT_SET')
      }
    });
  };

  const handleCancelEdit = () => {
    const newEdited = { ...editedContracts };
    delete newEdited[editingPlayer];
    setEditedContracts(newEdited);
    setEditingPlayer(null);
  };

  const handleContractChange = (leaguePlayerId, field, value) => {
    const updated = {
      ...editedContracts,
      [leaguePlayerId]: {
        ...editedContracts[leaguePlayerId],
        [field]: value
      }
    };

    if (field === 'contract_type') {
      updated[leaguePlayerId].contract_years = contractTypeToYears(value);
      if (value === 'none_minors') {
        updated[leaguePlayerId].salary = 0;
      }
    }

    setEditedContracts(updated);
  };

  const handleSaveEdit = async (leaguePlayerId) => {
    const changes = editedContracts[leaguePlayerId];
    if (!changes) return;

    const orig = contracts.find(c => c.ids?.league_player === leaguePlayerId);
    if (!orig) return;

    // Block save if contract type hasn't been set
    if (changes.contract_type === 'NOT_SET') {
      setError('Contract type must be set before saving. Please select a contract type.');
      setTimeout(() => setError(''), 5000);
      return;
    }

    const isMinors = changes.contract_type === 'none_minors';
    const contract = {
      league_player_id: leaguePlayerId,
      salary: isMinors ? 0 : changes.salary,
      contract_years: isMinors ? 0 : changes.contract_years,
      contract_type: isMinors ? null : (changes.contract_type || null),
      ...(isMinors && { acquisition_method: 'minor_league_draft' })
    };

    try {
      setSaving(true);
      setError('');
      setMessage('');

      const response = await leaguesAPI.commissionerOverrideContracts(
        leagueId,
        [contract]
      );

      if (response.success) {
        setMessage(`Updated contract for ${orig?.info?.full_name || 'player'}`);
        setTimeout(() => setMessage(''), 5000);

        const newEdited = { ...editedContracts };
        delete newEdited[leaguePlayerId];
        setEditedContracts(newEdited);
        setEditingPlayer(null);

        await loadTeamContracts(selectedTeamId);
      } else {
        setError(response.message || 'Failed to save contract');
        setTimeout(() => setError(''), 5000);
      }
    } catch (error) {
      console.error('Error saving contract:', error);
      setError('Failed to save contract');
      setTimeout(() => setError(''), 5000);
    } finally {
      setSaving(false);
    }
  };

  // Merge original contracts with edited values for display
  const displayContracts = contracts.map(contract => {
    const lpId = contract.ids?.league_player;
    const edits = lpId ? editedContracts[lpId] : null;
    if (!edits) return contract;

    return {
      ...contract,
      league_context: {
        ...contract.league_context,
        financial: {
          ...contract.league_context.financial,
          contract_salary: edits.salary ?? contract.league_context.financial.contract_salary,
          contract_years: edits.contract_years ?? contract.league_context.financial.contract_years,
          contract_type: edits.contract_type ?? contract.league_context.financial.contract_type
        }
      }
    };
  });

  // Create columns
  const columns = createCommissionerContractColumns({
    editingPlayer,
    editedContracts,
    onStartEdit: handleStartEdit,
    onCancelEdit: handleCancelEdit,
    onSaveEdit: handleSaveEdit,
    onContractChange: handleContractChange,
    salarySettings
  });

  const selectedTeam = teams.find(t => t.team_id === selectedTeamId);

  return (
    <div className="space-y-6">
      {/* Messages */}
      {message && (
        <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.success}/20 text-green-400 flex items-center gap-3`}>
          <Check className="w-5 h-5" />
          <span>{message}</span>
        </div>
      )}

      {error && (
        <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.error}/20 text-red-400 flex items-center gap-3`}>
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Team Selector */}
      <div className={dynastyTheme.components.card.base}>
        <div className="p-6">
          <label className={dynastyTheme.components.label}>
            Select Team
          </label>
          <select
            value={selectedTeamId}
            onChange={handleTeamChange}
            disabled={loading || saving}
            className={`${dynastyTheme.components.input} w-full max-w-md`}
          >
            <option value="">-- Select a Team --</option>
            {teams.map(team => (
              <option key={team.team_id} value={team.team_id}>
                {team.team_name}
              </option>
            ))}
          </select>

          {selectedTeam && (
            <div className="mt-4 flex items-center gap-4 text-sm">
              <span className={dynastyTheme.classes.text.neutralLight}>
                Team ID: <span className={dynastyTheme.classes.text.white}>{selectedTeam.team_id.slice(0, 8)}...</span>
              </span>
              <span className={dynastyTheme.classes.text.neutralLight}>
                Players: <span className={dynastyTheme.classes.text.white}>{contracts.length}</span>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Contract Summary Counts */}
      {selectedTeamId && contracts.length > 0 && (() => {
        const pendingCount = contracts.filter(c => {
          const fin = c.league_context?.financial || {};
          return !fin.contract_type && fin.contract_years === 0;
        }).length;
        const finalYearCount = contracts.filter(c => {
          const fin = c.league_context?.financial || {};
          return fin.contract_type === '1' && fin.contract_years === 1;
        }).length;

        if (pendingCount === 0 && finalYearCount === 0) return null;

        return (
          <div className={`${dynastyTheme.components.card.base} p-4`}>
            <div className="flex items-center gap-6 text-sm">
              {pendingCount > 0 && (
                <span className="flex items-center gap-2">
                  <span className="inline-block w-2.5 h-2.5 rounded-full bg-yellow-400" />
                  <span className="text-yellow-400 font-semibold">{pendingCount} Pending</span>
                  <span className={dynastyTheme.classes.text.neutralLight}>— need keeper decision</span>
                </span>
              )}
              {finalYearCount > 0 && (
                <span className="flex items-center gap-2">
                  <span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-400" />
                  <span className="text-amber-400 font-semibold">{finalYearCount} Final Year</span>
                </span>
              )}
            </div>
          </div>
        );
      })()}

      {/* Contracts Table */}
      {selectedTeamId && (
        <div className={dynastyTheme.components.card.base}>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className={dynastyTheme.components.heading.h3}>
                Team Contracts
              </h3>

              {saving && (
                <div className="flex items-center gap-2">
                  <div className="inline-block w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
                  <span className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>Saving...</span>
                </div>
              )}
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block w-8 h-8 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin mb-4" />
                <p className={dynastyTheme.classes.text.neutralLight}>Loading contracts...</p>
              </div>
            ) : contracts.length === 0 ? (
              <div className="text-center py-12">
                <p className={dynastyTheme.classes.text.neutralLight}>
                  No contracts found for this team
                </p>
              </div>
            ) : (
              <DynastyTable
                data={displayContracts}
                columns={columns}
                maxHeight="600px"
                enableVerticalScroll={true}
                enableHorizontalScroll={true}
              />
            )}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className={`${dynastyTheme.components.card.base} border-l-4 ${dynastyTheme.classes.border.warning}`}>
        <div className="p-6">
          <h4 className={`font-semibold mb-2 ${dynastyTheme.classes.text.warning}`}>
            How to Use
          </h4>
          <ul className={`space-y-2 text-sm ${dynastyTheme.classes.text.neutralLight}`}>
            <li>Select a team from the dropdown to view their contracts</li>
            <li>Click "Edit" on any player to modify their salary and contract type</li>
            <li>Salary options follow league increment rules ($2 increments)</li>
            <li>Contract type auto-sets contract years (OP=1yr, OP1=2yr, etc.)</li>
            <li>Click "Save" on the row to commit that player's contract immediately</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

/**
 * Position Eligibility Tab
 * Moved from PositionEligibility.js
 */
const PositionEligibilityTab = ({ leagueId, league, teams }) => {
  const [loading, setLoading] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState(teams?.[0]?.team_id || null);
  const [teamRoster, setTeamRoster] = useState([]);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Load roster when team changes
  useEffect(() => {
    if (selectedTeamId) {
      loadTeamRoster();
    }
  }, [selectedTeamId]);

  const loadTeamRoster = async () => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await leaguesAPI.getPositionEligibility(leagueId, selectedTeamId);

      if (response.success && response.teams?.length) {
        setTeamRoster(response.teams[0].players || []);
      } else {
        setTeamRoster([]);
      }
    } catch (err) {
      console.error('Failed to load roster:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load roster');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePlayer = async (update) => {
    try {
      const response = await leaguesAPI.updatePositionEligibility(leagueId, update);

      if (response.success) {
        setSuccessMessage(response.message || 'Player eligibility updated successfully');
        setEditingPlayer(null);

        // Reload roster to show changes
        await loadTeamRoster();

        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err) {
      console.error('Failed to update player:', err);
      throw err; // Re-throw so modal can handle it
    }
  };

  const columns = createPositionEligibilityColumns({
    onEditPlayer: setEditingPlayer,
    league
  });

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {successMessage && (
        <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
          <div className="flex items-center gap-2">
            <Shield className={`w-5 h-5 ${dynastyTheme.classes.text.success}`} />
            <p className={dynastyTheme.classes.text.success}>
              {successMessage}
            </p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
          <div className="flex items-center gap-2">
            <AlertCircle className={`w-5 h-5 ${dynastyTheme.classes.text.error}`} />
            <p className={dynastyTheme.classes.text.error}>
              {error}
            </p>
          </div>
        </div>
      )}

      {/* Team Selector */}
      <div className={dynastyTheme.components.card.base + ' p-4'}>
        <div className="flex items-center gap-4">
          <label className={dynastyTheme.components.label}>
            Select Team:
          </label>
          <select
            value={selectedTeamId || ''}
            onChange={(e) => setSelectedTeamId(e.target.value)}
            className={dynastyTheme.components.input + ' w-full max-w-md'}
            disabled={loading}
          >
            {teams && teams.map(team => (
              <option key={team.team_id} value={team.team_id}>
                {team.team_name} {team.manager_name ? `(${team.manager_name})` : ''}
              </option>
            ))}
          </select>
          {loading && (
            <Loader className={`w-5 h-5 animate-spin ${dynastyTheme.classes.text.primary}`} />
          )}
        </div>
      </div>

      {/* Roster Table */}
      <div className={dynastyTheme.components.card.base}>
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full mx-auto" />
            <p className={`mt-4 ${dynastyTheme.classes.text.neutralLight}`}>
              Loading roster...
            </p>
          </div>
        ) : teamRoster.length === 0 ? (
          <div className="p-12 text-center">
            <AlertCircle className={`w-12 h-12 mx-auto mb-4 ${dynastyTheme.classes.text.neutralDark}`} />
            <p className={dynastyTheme.classes.text.neutralLight}>
              No active players found for this team
            </p>
          </div>
        ) : (
          <DynastyTable
            data={teamRoster}
            columns={columns}
            tableType="flat-data"
            stickyHeader={true}
            enableHorizontalScroll={true}
            enableVerticalScroll={true}
            maxHeight="600px"
          />
        )}
      </div>

      {/* Usage Instructions */}
      <div className={`${dynastyTheme.components.card.base} border-l-4 ${dynastyTheme.classes.border.warning}`}>
        <div className="p-6">
          <h4 className={`font-semibold mb-2 ${dynastyTheme.classes.text.warning}`}>
            How to Use
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex gap-3">
              <span className={dynastyTheme.classes.text.primary}>1.</span>
              <p className={dynastyTheme.classes.text.neutralLight}>
                <strong>Select a team</strong> from the dropdown above to view their active roster
              </p>
            </div>
            <div className="flex gap-3">
              <span className={dynastyTheme.classes.text.primary}>2.</span>
              <p className={dynastyTheme.classes.text.neutralLight}>
                <strong>Click "Edit"</strong> on any player to grant them custom position eligibility
              </p>
            </div>
            <div className="flex gap-3">
              <span className={dynastyTheme.classes.text.primary}>3.</span>
              <p className={dynastyTheme.classes.text.neutralLight}>
                <strong>Select positions</strong> they should be eligible for (e.g., SS, MI, UTIL)
              </p>
            </div>
            <div className="flex gap-3">
              <span className={dynastyTheme.classes.text.primary}>4.</span>
              <p className={dynastyTheme.classes.text.neutralLight}>
                <strong>Add notes</strong> explaining why eligibility was granted (e.g., "Played SS in minors")
              </p>
            </div>
            <div className="flex gap-3">
              <span className={dynastyTheme.classes.text.primary}>5.</span>
              <p className={dynastyTheme.classes.text.neutralLight}>
                Team owners can now assign the player to any granted position slots
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editingPlayer && (
        <PlayerEligibilityModal
          player={editingPlayer}
          league={league}
          onSave={handleUpdatePlayer}
          onClose={() => setEditingPlayer(null)}
        />
      )}
    </div>
  );
};

export default ManualOverrides;
